import { onBeforeUnmount, onMounted, ref } from "@vue/composition-api";

interface ScrollOptions {
    /**
     * The treshold to change processing of the scroll event
     */
    directionChangeTreshold: number;
}

interface ScrollState {
    /**
     * The current scroll top position
     */
    scrollTop: number;
    /**
     * The current direction of the scroll event
     */
    direction: "up" | "down" | undefined;
    /**
     * The total difference in scroll position since the last direction change
     */
    scrollDifference: number;
}

// composable to handle scroll event
export const useScroll = (options: ScrollOptions = { directionChangeTreshold: 0 }) => {
    /**
     * cache last scroll state to compare with new state
     */
    const lastState = ref<ScrollState>({
        scrollTop: 0,
        direction: null,
        scrollDifference: 0
    });
    const direction = ref<"up" | "down" | undefined>(undefined);

    /**
     * The event handler for the scroll event
     */
    const handleScroll = () => {
        const newScrollTop = window.scrollY || document.documentElement.scrollTop;
        const newDirection = newScrollTop < lastState.value.scrollTop ? "up" : "down";
        const newScrollDifference = Math.abs(newScrollTop - lastState.value.scrollTop);

        //if the direction has changed, we reset the scroll difference
        if (newDirection != lastState.value.direction) {
            lastState.value.scrollDifference = newScrollDifference;
            lastState.value.direction = newDirection;
        }
        //if the direction is the same, we add the difference to the total difference
        else {
            lastState.value.scrollDifference += newScrollDifference;
        }

        //if the difference is bigger than the treshold, we change the direction
        if (lastState.value.scrollDifference > options.directionChangeTreshold) {
            direction.value = newDirection;
        }

        //allways update the scrolltop to compare to new state
        lastState.value.scrollTop = newScrollTop;
    };

    /**
     * Add the event listener when the component is mounted
     */
    onMounted(() => {
        window?.addEventListener("scroll", handleScroll);
    });

    /**
     * Remove the event listener when the component is unmounted
     */
    onBeforeUnmount(() => {
        window?.removeEventListener("scroll", handleScroll);
    });

    return {
        direction
    };
};
